import React, { useState } from "react";
import { Auth } from "@aws-amplify/auth";
import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";

import { Unionize } from "fond/types";
import { CognitoResponseCode, isCognitoError } from "fond/utils/auth";
import { NavButton } from "fond/widgets/Link/NavButton";
import { NavLink } from "fond/widgets/Link/NavLink";
import { PageCentred } from "fond/widgets/PageCentred";

import SignInAdornment from "./SignInAdornment";

interface IProps {
  email: string;
}

const ResendState = ["RESEND_SUCCESS", "ALREADY_CONFIRMED", "UNKNOWN_ERROR"] as const;
type ResendState = Unionize<typeof ResendState>;

/**
 * A resend email component that uses @aws-amplify/auth
 * to resend an email to a newly signed up user
 */
const ResendEmail: React.FC<IProps> = ({ email }) => {
  const [resendState, setResendState] = useState<ResendState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleResendVerificationEmail = async () => {
    try {
      await Auth.resendSignUp(email.toLowerCase());
      setResendState("RESEND_SUCCESS");
    } catch (err) {
      if (isCognitoError(err)) {
        if (err.code === CognitoResponseCode.INVALID_PARAMETER && err.message.includes("already confirmed")) {
          setResendState("ALREADY_CONFIRMED");
          return;
        }
        setErrorMessage(err.message);
      }
      setResendState("UNKNOWN_ERROR");
      console.error(err);
      Sentry.captureException(err);
    }
  };

  return (
    <PageCentred
      title="Check your email"
      description={
        <Typography data-testid="success-message">
          Thanks! You're almost done. Click the link we have emailed you to finish creating your account.
        </Typography>
      }
      rightAdornment={<SignInAdornment />}
    >
      {resendState === "RESEND_SUCCESS" && (
        <Alert severity="success" sx={{ mt: 4 }}>
          <AlertTitle>Verification email sent!</AlertTitle>
          Click on the link we've emailed you to verify your email.
        </Alert>
      )}
      {resendState === "ALREADY_CONFIRMED" && (
        <Alert severity="info" sx={{ mt: 4 }}>
          <AlertTitle>User already confirmed</AlertTitle>
          <p>
            The user <i>{email}</i> has already been confirmed.
          </p>
          <p>
            You can now <NavLink to="/signin">log in</NavLink>.
          </p>
        </Alert>
      )}
      {resendState === "UNKNOWN_ERROR" && (
        <Alert severity="error" sx={{ mt: 4 }}>
          <AlertTitle>An error occurred when resending</AlertTitle>
          <p>We could not resend the confirmation for this user.</p>
          {errorMessage && <p>{errorMessage}</p>}
        </Alert>
      )}
      {resendState === null && (
        <Button fullWidth variant="contained" color="primary" onClick={handleResendVerificationEmail}>
          Resend verification
        </Button>
      )}
      <Box sx={{ mt: 4, textAlign: "right" }}>
        <NavButton variant="text" to="/signin">
          Back to Log In
        </NavButton>
      </Box>
    </PageCentred>
  );
};

export default ResendEmail;
